<template lang="pug">
el-form(
  ref="formRef"
  :model="form"
  :rules="rules"
  label-position="top"
  @submit.prevent="handleSubmit"
).c-glow-edit-form
  app-main-content-layout(:title="isEditMode ? 'Glow Detail' : 'Create Glow'" back-to="/manage-content/media/glows")
    template(#actions)
      el-button(type="default" plain @click="goToGlowList").button-dark-outlined.min-w-110
        fai(type="fal" icon="times").size-18
        span Cancel
      el-button(type="primary" @click="handleSubmit" :loading="loading" color="#1EBEAF").has-fai.min-w-110
        fai(type="fal" icon="save").size-18
        span {{ isEditMode ? 'Save' : 'Create' }}

    template(#col-left)
      .c-content-section
        // Organization context display
        .org-context-banner(v-if="glowOrganization")
          .org-context-content
            img.org-avatar(
              v-if="glowOrganization.avatar"
              :src="glowOrganization.avatar"
            )
            .org-avatar-placeholder(v-else)
            .org-context-text
              span.org-context-label Creating glow for:
              strong.org-context-name {{ glowOrganization.name }}

        el-form-item(label="Title" prop="title")
          el-input(
            v-model="form.title"
            placeholder="Enter glow title"
            maxlength="100"
          )

        el-form-item(label="Slug" prop="slug")
          template(#label)
            | Slug
            el-tooltip(
              effect="customized"
              :content="slugTip"
              placement="top"
              popper-class="max-w-350"
            )
              fai(type="fal" icon="info-circle").size-18.ms-2
          el-input(
            v-model="form.slug"
            placeholder="your-glow-name"
            @input="sanitizeSlug"
          )

        el-form-item(label="Description" prop="description")
          el-input(
            v-model="form.description"
            type="textarea"
            :rows="4"
            placeholder="Enter glow description"
            maxlength="500"
          )

        el-form-item(label="Geolocation (Optional)" prop="geolocation")
          el-input(
            v-model="form.geolocation"
            placeholder="Enter geolocation (e.g., City, State or coordinates)"
          )

        el-form-item(label="Post-Event Message (Optional)" prop="postEventMessage")
          el-input(
            v-model="form.postEventMessage"
            type="textarea"
            :rows="3"
            placeholder="Enter a message to display after the glow event ends"
            maxlength="500"
          )

        el-form-item(label="Glow Dates & Color").mb-0
          GlowDatePicker(v-model="glowDates")

      .c-content-section.mt-3
        h5.section-title Participation Type

        el-form-item(label="Participation Type")
          el-radio-group(v-model="participationType").radio-group-horizontal
            el-radio(label="free") No CTA
            el-radio(label="donation") Donate (select org)
            el-radio(label="sticker") Sticker (select sticker)

        el-form-item(v-if="participationType !== 'free'")
          el-checkbox(v-model="form.requireCtaToUnlock")
            span Require CTA to Unlock

        // Organization CTA
        el-form-item(label="Select Organization" v-if="participationType === 'donation'")
          .cta-selector
            .selected-item(v-if="form.ctaOrganizationId && selectedOrganization")
              .selected-item-content
                .selected-item-thumbnail
                  img(
                    v-if="selectedOrganization.avatar"
                    :src="selectedOrganization.avatar"
                  )
                  .no-image(v-else)
                .selected-item-info
                  .selected-item-title {{ selectedOrganization.name }}
              .selected-item-actions
                el-button(
                  type="primary"
                  size="small"
                  @click="showOrganizationSelector = true"
                ) Change
                el-button(type="danger" size="small" plain @click="clearOrganization") Clear

            .no-item-selected(v-else)
              el-button(type="primary" plain @click="showOrganizationSelector = true")
                fai(type="fal" icon="plus-circle")
                | Select Organization

        // Sticker CTA
        el-form-item(label="Select Sticker" v-if="participationType === 'sticker'")
          .cta-selector
            .selected-item(v-if="form.ctaStickerId && selectedSticker")
              .selected-item-content
                .selected-item-thumbnail
                  img(
                    v-if="selectedSticker.url"
                    :src="selectedSticker.url"
                  )
                  .no-image(v-else)
                .selected-item-info
                  .selected-item-title {{ selectedSticker.title }}
                  .selected-item-creator(
                    v-if="getStickerCreator(selectedSticker)"
                  ) {{ getStickerCreator(selectedSticker) }}
              .selected-item-actions
                el-button(
                  type="primary"
                  size="small"
                  @click="showStickerSelector = true"
                ) Change
                el-button(type="danger" size="small" plain @click="clearSticker") Clear

            .no-item-selected(v-else)
              el-button(type="primary" plain @click="showStickerSelector = true")
                fai(type="fal" icon="plus-circle")
                | Select Sticker

      .c-content-section.mt-3
        h5.section-title Question (Optional)

        el-form-item(label="Select or Create Question")
          .cta-selector
            .selected-item(v-if="form.ctaQuestionId && selectedQuestion")
              .selected-item-content
                .selected-item-info
                  .selected-item-title {{ selectedQuestion.content }}
                  .selected-item-date(v-if="selectedQuestion.createdAt")
                    | {{ formatDate(selectedQuestion.createdAt) }}
              .selected-item-actions
                el-button(
                  type="primary"
                  size="small"
                  @click="showQuestionSelector = true"
                ) Change
                el-button(type="danger" size="small" plain @click="clearQuestion") Clear

            .no-item-selected(v-else)
              el-button(type="primary" plain @click="showQuestionSelector = true")
                fai(type="fal" icon="plus-circle")
                | Select or Create Question

    template(#col-right)
      .c-content-section
        h5.section-title Analytics Options

        .analytics-checkboxes
          el-form-item.mb-2
            el-checkbox(v-model="form.showAmountRaised")
              span Show Amount Raised

          el-form-item.mb-2
            el-checkbox(v-model="form.showTotalViews")
              span Show Total Views

          el-form-item.mb-2
            el-checkbox(v-model="form.showTotalVoices")
              span Show Total Voices

      .c-content-section.mt-3(v-if="isEditMode && glowUrl")
        h5.section-title Share Glow
        
        .qr-code-section
          qrcode-vue(:value="glowUrl" :size="220")
        
        el-form-item(label="Share Link").mt-3
          .link-input-group
            el-input(:value="glowUrl" readonly size="small")
            el-button(type="primary" size="small" @click="copyShareLink")
              span Copy

  // Dialogs
  DialogSelectSingleOrganization(
    v-model="showOrganizationSelector"
    :preSelectedOrganization="form.ctaOrganizationId"
    @confirm="handleOrganizationSelected"
    @clear="clearOrganization"
  )

  DialogSelectSingleQuestion(
    v-model="showQuestionSelector"
    :preSelectedQuestion="form.ctaQuestionId"
    :orgId="glowOrganization?.id"
    @confirm="handleQuestionSelected"
    @clear="clearQuestion"
  )

  DialogSelectSingleSticker(
    v-model="showStickerSelector"
    :preSelectedSticker="form.ctaStickerId"
    @confirm="handleStickerSelected"
    @clear="clearSticker"
  )
</template>

<script lang="ts">
import {
  defineComponent, ref, reactive, computed, onMounted, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import QrcodeVue from 'qrcode.vue';
import GlowDatePicker from './components/GlowDatePicker.vue';
import DialogSelectSingleOrganization from '@/components/DialogSelectSingleOrganization.vue';
import DialogSelectSingleQuestion from './components/DialogSelectSingleQuestion.vue';
import DialogSelectSingleSticker from './components/DialogSelectSingleSticker.vue';
import glowShared from './shared';
import { followTitleSlug } from '@/utils/slug';

export default defineComponent({
  name: 'CreateGlow',
  components: {
    GlowDatePicker,
    DialogSelectSingleOrganization,
    DialogSelectSingleQuestion,
    DialogSelectSingleSticker,
    QrcodeVue,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { createGlow, updateGlow, getGlow } = glowShared();
    const formRef = ref();
    const loading = ref(false);
    const glowId = computed(() => route.params.id as string);
    const isEditMode = computed(() => !!glowId.value);

    // Organization context for glow creation
    const glowOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

    // Store the glow's own organization ID for fallback when menu org is not selected
    const loadedGlowOrgId = ref<string>('');

    const form = reactive({
      title: '',
      slug: '',
      description: '',
      geolocation: '',
      postEventMessage: '',
      showAmountRaised: false,
      showTotalViews: false,
      showTotalVoices: false,
      requireCtaToUnlock: false,
      ctaOrganizationId: '',
      ctaQuestionId: '',
      ctaStickerId: '',
    });

    const glowDates = ref({
      startDate: '',
      endDate: '',
      startTimezone: '',
      endTimezone: '',
      color: '#F8E587',
    });

    // Participation Type selection
    const participationType = ref('free');

    // Dialog visibility
    const showOrganizationSelector = ref(false);
    const showQuestionSelector = ref(false);
    const showStickerSelector = ref(false);

    // Selected items data
    const selectedOrganization = ref(null);
    const selectedQuestion = ref(null);
    const selectedSticker = ref(null);

    // Note: Participation Type determines the main CTA type (FREE, DONATION, STICKER)
    // Question is now a separate optional field independent of participation type
    // Selections are preserved when switching between participation types
    // Only the selected participationType and its corresponding CTA will be sent to backend on save

    const rules = {
      title: [
        { required: true, message: 'Please enter a title', trigger: 'blur' },
        {
          min: 3,
          max: 100,
          message: 'Title must be between 3 and 100 characters',
          trigger: 'blur',
        },
      ],
      slug: [
        { required: true, message: 'Please enter a slug', trigger: 'blur' },
        {
          pattern: /^[a-z0-9-]+$/,
          message: 'Slug must contain only lowercase letters, numbers, and hyphens',
          trigger: 'blur',
        },
      ],
      description: [
        {
          max: 500,
          message: 'Description must not exceed 500 characters',
          trigger: 'blur',
        },
      ],
      postEventMessage: [
        {
          max: 500,
          message: 'Post-event message must not exceed 500 characters',
          trigger: 'blur',
        },
      ],
    };

    const goToGlowList = () => {
      router.push('/manage-content/media/glows');
    };

    // New glows: the slug follows the title until it's edited by hand (SPO-1215)
    let lastAutoSlug = '';
    watch(() => form.title, (title) => {
      if (isEditMode.value) return;
      const slug = followTitleSlug(title, form.slug, lastAutoSlug);
      if (slug !== form.slug) {
        form.slug = slug;
        lastAutoSlug = slug;
      }
    });

    // Function to sanitize slug input - only allow lowercase letters, numbers, and hyphens
    const sanitizeSlug = () => {
      form.slug = form.slug.replace(/[^a-z0-9-]/g, '').toLowerCase();
    };

    // Computed property for slug tooltip
    const slugTip = computed(() => 'A slug is a URL-friendly version of your glow name. It creates clean, memorable URLs like "spotlight.xyz/web/glow/your-glow-name". '
      + 'Slugs improve SEO, make sharing easier, and provide a professional appearance. Use lowercase letters, numbers, and hyphens only.');

    // Computed property for glow URL
    const glowUrl = computed(() => {
      if (!isEditMode.value || !glowId.value) return '';
      const baseUrl = import.meta.env.VITE_WEB_BASE_URL || '';
      const identifier = form.slug || glowId.value;
      return `${baseUrl}/web/glow/${identifier}`;
    });

    // Function to copy share link
    const copyShareLink = async () => {
      try {
        const url = glowUrl.value;

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          ElMessage.success('Share link copied to clipboard!');
        } else {
          // Fallback for older browsers or when clipboard API is not available
          const textArea = document.createElement('textarea');
          textArea.value = url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            document.execCommand('copy');
            ElMessage.success('Share link copied to clipboard!');
          } catch (err) {
            console.error('Fallback copy failed:', err);
            ElMessage.error('Failed to copy share link');
          } finally {
            document.body.removeChild(textArea);
          }
        }
      } catch (error) {
        console.error('Failed to copy share link:', error);
        ElMessage.error('Failed to copy share link');
      }
    };

    // Handle organization selection
    const handleOrganizationSelected = (organizationId: string, organizationObject: any) => {
      form.ctaOrganizationId = organizationId;
      selectedOrganization.value = organizationObject;
    };

    const clearOrganization = () => {
      form.ctaOrganizationId = '';
      selectedOrganization.value = null;
    };

    // Handle question selection
    const handleQuestionSelected = (questionId: string, questionObject: any) => {
      form.ctaQuestionId = questionId;
      selectedQuestion.value = questionObject;
    };

    const clearQuestion = () => {
      form.ctaQuestionId = '';
      selectedQuestion.value = null;
    };

    // Handle sticker selection
    const handleStickerSelected = (stickerId: string, stickerObject: any) => {
      form.ctaStickerId = stickerId;
      selectedSticker.value = stickerObject;
    };

    const clearSticker = () => {
      form.ctaStickerId = '';
      selectedSticker.value = null;
    };

    // Get sticker creator name
    const getStickerCreator = (sticker: any) => {
      if (!sticker) return '';
      if (sticker.organization) return sticker.organization.name;
      if (sticker.user) return `${sticker.user.firstname} ${sticker.user.lastname}`;
      return '';
    };

    // Format date
    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    const validateOrganization = () => {
      // For creating new glows, organization must be selected from menu
      if (!isEditMode.value && !glowOrganization.value) {
        ElMessage.error('Please select an organization from the dropdown menu');
        return false;
      }
      // For editing, we can use the loaded glow's organization as fallback
      if (isEditMode.value && !glowOrganization.value && !loadedGlowOrgId.value) {
        ElMessage.error('Organization information is missing');
        return false;
      }
      return true;
    };

    const validateGlowDates = () => {
      if (!glowDates.value || typeof glowDates.value !== 'object') {
        ElMessage.error('Please configure glow dates');
        return false;
      }

      // Check timezone first, since if timezone is missing, dates will be empty strings
      // (the GlowDatePicker only computes UTC dates when timezone is present)
      if (!glowDates.value.startTimezone) {
        ElMessage.error('Please select a timezone');
        return false;
      }

      if (!glowDates.value.startDate || !glowDates.value.endDate) {
        ElMessage.error('Please select glow start and end dates');
        return false;
      }

      return true;
    };

    const validateCtaSelection = () => {
      // If participation type is FREE, no CTA validation needed
      if (participationType.value === 'free') {
        return true;
      }

      // If participation type is DONATION, organization must be selected
      if (participationType.value === 'donation' && !form.ctaOrganizationId) {
        ElMessage.error('Please select an organization for donation');
        return false;
      }

      // If participation type is STICKER, sticker must be selected
      if (participationType.value === 'sticker' && !form.ctaStickerId) {
        ElMessage.error('Please select a sticker');
        return false;
      }

      return true;
    };

    const loadGlowData = async () => {
      if (!isEditMode.value) return;

      try {
        loading.value = true;

        // Get orgId from query params (passed from list), menu selection, or use undefined
        const queryOrgId = route.query.glowOrgId as string;
        const orgIdForQuery = glowOrganization.value?.id || queryOrgId;

        const glowData = await getGlow(glowId.value, orgIdForQuery);

        if (!glowData) {
          ElMessage.error('Glow not found');
          router.push('/manage-content/media/glows');
          return;
        }

        // Store the glow's organization ID for later use
        if (glowData.organization?.id) {
          loadedGlowOrgId.value = glowData.organization.id;
        }

        // Populate form fields
        form.title = glowData.title || '';
        form.slug = glowData.slug || '';
        form.description = glowData.description || '';
        form.geolocation = glowData.geolocation || '';
        form.postEventMessage = glowData.postEventMessage || '';
        form.showAmountRaised = glowData.showAmountRaised || false;
        form.showTotalViews = glowData.showTotalViews || false;
        form.showTotalVoices = glowData.showTotalVoices || false;
        form.requireCtaToUnlock = glowData.requireCtaToUnlock || false;

        // Populate glow dates
        glowDates.value = {
          startDate: glowData.glowStartDate || '',
          endDate: glowData.glowEndDate || '',
          startTimezone: glowData.glowStartDateTimeZone || '',
          endTimezone: glowData.glowEndDateTimeZone || '',
          color: glowData.glowColor || '#F8E587',
        };

        // Populate CTA data based on the participationType field from backend
        // The backend may return all CTA objects, but only one is active based on participationType
        if (glowData.participationType) {
          const participationTypeMap = {
            FREE: 'free',
            DONATION: 'donation',
            STICKER: 'sticker',
          };
          participationType.value = participationTypeMap[glowData.participationType] || 'free';
        }

        // Populate all CTA data (but only the one matching participationType will be used)
        if (glowData.ctaOrganization) {
          form.ctaOrganizationId = glowData.ctaOrganization.id;
          selectedOrganization.value = glowData.ctaOrganization;
        }
        if (glowData.ctaQuestion) {
          form.ctaQuestionId = glowData.ctaQuestion.id;
          selectedQuestion.value = glowData.ctaQuestion;
        }
        if (glowData.ctaSticker) {
          form.ctaStickerId = glowData.ctaSticker.id;
          selectedSticker.value = glowData.ctaSticker;
        }
      } catch (error) {
        console.error('Error loading glow:', error);
        ElMessage.error('Failed to load glow data');
        router.push('/manage-content/media/glows');
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      try {
        await formRef.value.validate();

        // Validate organization first
        if (!validateOrganization()) {
          return;
        }

        if (!validateGlowDates()) {
          return;
        }

        if (!validateCtaSelection()) {
          return;
        }

        loading.value = true;

        // Only send the CTA ID that matches the selected participationType, set others to null
        let ctaOrganizationId: string | null = null;
        let ctaQuestionId: string | null = null;
        let ctaStickerId: string | null = null;

        if (participationType.value === 'donation') {
          ctaOrganizationId = form.ctaOrganizationId || null;
        } else if (participationType.value === 'sticker') {
          ctaStickerId = form.ctaStickerId || null;
        }

        // Question is now optional and independent of participationType
        if (form.ctaQuestionId) {
          ctaQuestionId = form.ctaQuestionId;
        }

        const input: any = {
          title: form.title,
          slug: form.slug,
          description: form.description,
          glowColor: glowDates.value.color,
          geolocation: form.geolocation || null,
          postEventMessage: form.postEventMessage?.trim() || null,
          glowStartDate: glowDates.value.startDate,
          glowStartDateTimeZone: glowDates.value.startTimezone,
          glowEndDate: glowDates.value.endDate,
          glowEndDateTimeZone: glowDates.value.endTimezone,
          requireCtaToUnlock: form.requireCtaToUnlock,
          showAmountRaised: form.showAmountRaised,
          showTotalViews: form.showTotalViews,
          showTotalVoices: form.showTotalVoices,
          ctaOrganizationId,
          ctaQuestionId,
          ctaStickerId,
        };

        // Add id for edit mode
        if (isEditMode.value) {
          input.id = glowId.value;
        }

        // Get orgId: use menu selection or fall back to loaded glow's org ID
        const orgIdForMutation = glowOrganization.value?.id || loadedGlowOrgId.value;

        if (!orgIdForMutation) {
          throw new Error('Organization ID is required');
        }

        // Pass orgId to create/update functions
        await (isEditMode.value
          ? updateGlow(orgIdForMutation, input)
          : createGlow(orgIdForMutation, input));

        ElMessage.success(`Glow ${isEditMode.value ? 'updated' : 'created'} successfully!`);

        // Return to the tab we came from, if specified
        const returnTab = route.query.returnTab as string;
        if (returnTab) {
          router.push({
            path: '/manage-content/media/glows',
            query: { returnTab },
          });
        } else {
          router.push('/manage-content/media/glows');
        }
      } catch (error) {
        console.error(`Error ${isEditMode.value ? 'updating' : 'creating'} glow:`, error);

        // Display the specific error message from the backend if available
        const errorMessage = error instanceof Error && error.message
          ? error.message
          : `Failed to ${isEditMode.value ? 'update' : 'create'} glow. Please try again.`;

        ElMessage.error(errorMessage);
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      await loadGlowData();
    });

    return {
      formRef,
      form,
      rules,
      glowDates,
      loading,
      isEditMode,
      participationType,
      showOrganizationSelector,
      showQuestionSelector,
      showStickerSelector,
      glowOrganization,
      selectedOrganization,
      selectedQuestion,
      selectedSticker,
      goToGlowList,
      handleSubmit,
      handleOrganizationSelected,
      clearOrganization,
      handleQuestionSelected,
      clearQuestion,
      handleStickerSelected,
      clearSticker,
      getStickerCreator,
      formatDate,
      sanitizeSlug,
      slugTip,
      glowUrl,
      copyShareLink,
      validateOrganization,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables - Match UsersEdit.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

// Main Form Container
.c-glow-edit-form {
  padding: 2rem;
  
  :deep(.grid-2-cols) {
    gap: 1.5rem;
  }
  
  :deep(.col-has-child) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// White Content Sections
.c-content-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// Section Title
.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: $gray-900;
  margin-bottom: 1rem;
}

// Organization Context Banner
.org-context-banner {
  background-color: #E0E7FF;
  border: 1px solid #818CF8;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
}

.org-context-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.org-context-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.org-context-label {
  font-size: 0.875rem;
  color: $gray-700;
}

.org-context-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: $gray-900;
}

// Reduce gap between analytics checkboxes
.analytics-checkboxes {
  :deep(.el-form-item) {
    margin-bottom: 0.5rem;
  }
}

// CTA Selector Styles
.cta-selector {
  margin-top: 0.5rem;
}

.selected-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid $gray-200;
  border-radius: 12px;
  background-color: $gray-50;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: $color-purple-text;
    background-color: color.adjust($color-purple-text, $lightness: 48%);
  }
}

.selected-item-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.selected-item-thumbnail {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid $gray-200;
}

.selected-item-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $gray-200;
  color: $gray-500;
  font-size: 0.75rem;
}

.selected-item-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 0.25rem;
}

.selected-item-title {
  font-weight: 600;
  color: $gray-900;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-item-date,
.selected-item-creator {
  font-size: 0.75rem;
  color: $gray-500;
}

.selected-item-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.no-item-selected {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
  border: 1px dashed $gray-300;
  border-radius: 12px;
  background-color: $gray-50;
}

// Link Input Group Styling
.link-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.link-input-group .el-input {
  flex: 1;
}

// QR Code Section
.qr-code-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  background-color: white;
  border-radius: 12px;
  border: 1px solid $gray-200;
}

// Organization display styles
.org-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #818CF8;
  flex-shrink: 0;
}

.org-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #C7D2FE;
  border: 2px solid #818CF8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #64748b;
  font-size: 14px;

  &:after {
    content: '👥';
  }
}

.mb-4 {
  margin-bottom: 1.5rem;
}

// Form Styling
:deep(.el-form-item) {
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }

  .el-form-item__label {
    color: $gray-700;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .el-input__wrapper {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 0 12px;
    height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover:not(.is-disabled) {
      border-color: #c0c4cc;
      box-shadow: none;
    }

    &.is-focus {
      border-color: #01beb0;
      box-shadow: none;
    }

    &.is-disabled {
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
    }
  }

  .el-input__inner {
    height: 100%;
    font-size: 0.875rem;
    color: $gray-700;
    background: transparent;
    font-weight: 500;

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }

  .el-textarea__inner {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 12px;
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 100px;

    &:hover {
      border-color: #c0c4cc;
      box-shadow: none;
    }

    &:focus {
      border-color: #01beb0;
      box-shadow: none;
    }

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }
}

// Button Styling
:deep(.el-button) {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  height: 36px;
  padding: 0 20px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-width: 1px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem;

  > span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .fai,
  svg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 0.5rem;
  }

  &.min-w-110 {
    min-width: 110px;
  }

  &.el-button--primary {
    background-color: $color-teal-accent;
    border-color: $color-teal-accent;
    color: white;

    &:hover,
    &:focus {
      background-color: color.adjust($color-teal-accent, $lightness: -5%);
      border-color: color.adjust($color-teal-accent, $lightness: -5%);
      box-shadow: 0 2px 4px 0 rgba(1, 190, 176, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      background-color: color.adjust($color-teal-accent, $lightness: -8%);
      border-color: color.adjust($color-teal-accent, $lightness: -8%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(1, 190, 176, 0.2);
    }
  }

  &.el-button--danger {
    &.is-plain {
      background-color: white;
      border-color: #f56c6c;
      color: #f56c6c;

      &:hover {
        background-color: #fef0f0;
        border-color: #f56c6c;
        color: #f56c6c;
        box-shadow: 0 2px 4px 0 rgba(245, 108, 108, 0.2);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  &.el-button--default {
    background-color: white;
    border-color: $gray-200;
    color: $gray-700;

    &:hover {
      background-color: $gray-50;
      border-color: $gray-300;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.button-dark-outlined {
    background-color: white;
    border-color: $gray-700;
    color: $gray-700;

    &:hover {
      background-color: $gray-50;
      border-color: $gray-900;
      color: $gray-900;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }

    &:active {
      background-color: $gray-100;
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
    }
  }

  &.el-button--small {
    padding: 0 12px;
    font-size: 0.8125rem;
    height: 32px;
  }
}

// Radio Group Styling
:deep(.el-radio-group) {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &.radio-group-horizontal {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1.5rem;
  }
}

:deep(.el-radio) {
  margin-right: 0;
  
  .el-radio__label {
    font-size: 0.875rem;
    color: $gray-700;
    font-weight: 500;
  }

  &.is-checked {
    .el-radio__label {
      color: $color-purple-text;
    }
  }
}

// Checkbox Styling
:deep(.el-checkbox) {
  .el-checkbox__label {
    font-size: 0.875rem;
    color: $gray-700;
    font-weight: 500;
  }
}

// Tooltip Styling
:deep(.el-tooltip__trigger) {
  .size-18 {
    color: $gray-400;
    transition: color 0.2s ease;

    &:hover {
      color: $gray-600;
    }
  }
}

// Page Background
:deep(.app-main-content-layout) {
  background-color: $gray-50;
}

// Utility Classes
.mt-3 {
  margin-top: 1rem;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

// Responsive Design
@media (max-width: 768px) {
  .c-glow-edit-form {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  :deep(.grid-2-cols) {
    gap: 1rem;
  }

  :deep(.el-form-item) {
    margin-bottom: 1.25rem;

    .el-form-item__label {
      font-size: 0.8125rem;
    }

    .el-input__inner,
    .el-textarea__inner {
      font-size: 0.8125rem;
    }
  }

  :deep(.el-button) {
    padding: 8px 16px;
    font-size: 0.8125rem;
  }

  .link-input-group {
    flex-direction: column;
    align-items: stretch;

    .el-input {
      width: 100%;
    }

    .el-button {
      width: 100%;
    }
  }

  .selected-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .selected-item-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 576px) {
  .c-glow-edit-form {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }
}
</style>
